import {
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AdinkraSymbol, type AdinkraName } from "../../components/common/GhanaMotifs";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";

const FEATURES = [
  {
    symbol: "neaonnim",
    title: "Create screenings in minutes",
    body: "Set tiers, perks, capacity, and sale windows. Publish to the marketplace or keep it unlisted.",
  },
  {
    symbol: "nkyinkyim",
    title: "Sell across many channels",
    body: "Your screening page, embed link, and the marketplace — all sharing one inventory.",
  },
  {
    symbol: "adinkrahene",
    title: "Live sales & attendees",
    body: "See tier-by-tier sales velocity, revenue, and check-ins as they happen.",
  },
  {
    symbol: "eban",
    title: "Validate at the gate",
    body: "Scan QR tickets from the same app — block duplicates and track entries in real time.",
  },
  {
    symbol: "fawohodie",
    title: "Get paid faster",
    body: "Mobile money, card, and bank transfer. Every payment tied back to its screening.",
  },
  {
    symbol: "dwennimmen",
    title: "Trust by review",
    body: "Every exhibitor is reviewed by our team — keeping the marketplace high-quality.",
  },
] satisfies Array<{ symbol: AdinkraName; title: string; body: string }>;

const STEPS = [
  {
    step: "1",
    title: "Apply",
    body: "Tell us about your organization and the screenings you run. Takes about 2 minutes.",
  },
  {
    step: "2",
    title: "Get reviewed",
    body: "We review every application within 1–2 business days and email you the decision.",
  },
  {
    step: "3",
    title: "Publish & sell",
    body: "Once approved, sign in, create your first screening, and start selling immediately.",
  },
];

export default function ForOrganizersPage() {
  return (
    <div className="min-h-screen bg-surface">
      <Navbar />

      <section className="relative overflow-hidden pb-20 pt-28 sm:pt-32 lg:pt-36">
        <div
          className="absolute inset-0 bg-gradient-to-br from-[#FFF9E6] via-white to-[#FFE6F5] dark:hidden"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 hidden bg-[radial-gradient(circle_at_top_left,rgba(245,179,3,0.14),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(46,196,182,0.08),transparent_30%),linear-gradient(135deg,#0f1217_0%,#171b22_52%,#10151d_100%)] dark:block"
          aria-hidden="true"
        />
        <div className="section-frame relative z-10">
          <div className="mx-auto max-w-4xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9a3412] dark:text-[#fdba74] shadow-sm">
              <AdinkraSymbol name="adinkrahene" className="h-3.5 w-3.5" />
              For cinema & content exhibitors
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-[56px]">
              A self-service platform for selling tickets and running great screenings.
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
              Publish your screening, sell across channels, track sales and attendees, validate at the
              gate, and receive payouts — all from one dashboard.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/exhibitor/signup"
                className="pill-button gold-glow bg-[var(--pp-gold)] px-6 py-4 text-base text-[var(--pp-charcoal)]"
              >
                Get started
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/exhibitor/status"
                className="pill-button bg-[var(--pp-charcoal)] px-6 py-4 text-base text-white"
              >
                Check application status
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="section-frame">
          <div className="mb-10 max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              What you get
            </div>
            <h2 className="mt-2 text-3xl font-bold text-foreground sm:text-4xl">
              Everything you need to run a ticketed screening.
            </h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature) => (
              <div key={feature.title} className="surface-card p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--pp-charcoal)] text-[var(--pp-gold-soft)]">
                  <AdinkraSymbol name={feature.symbol} className="h-7 w-7" strokeWidth={4.5} />
                </div>
                <h3 className="text-lg font-bold text-foreground">{feature.title}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{feature.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-surface-muted py-20">
        <div className="section-frame">
          <div className="mb-10 max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              How it works
            </div>
            <h2 className="mt-2 text-3xl font-bold text-foreground sm:text-4xl">
              From sign-up to selling in three steps.
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {STEPS.map((step) => (
              <div key={step.step} className="surface-card p-6">
                <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--pp-gold)] text-sm font-bold text-foreground">
                  {step.step}
                </div>
                <h3 className="text-lg font-bold text-foreground">{step.title}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="section-frame">
          <div className="mx-auto max-w-3xl rounded-3xl bg-[var(--pp-charcoal)] px-8 py-12 text-center text-white">
            <AdinkraSymbol name="binkabi" className="mx-auto h-10 w-10 text-[var(--pp-gold-soft)]" strokeWidth={4.5} />
            <h2 className="mt-4 text-3xl font-bold sm:text-4xl">Ready to run your next screening?</h2>
            <p className="mt-3 text-sm leading-7 text-slate-200">
              Set up your exhibitor account today. Our team reviews applications fast so you can
              start selling sooner.
            </p>
            <Link
              to="/exhibitor/signup"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--pp-gold)] px-6 py-3 text-sm font-bold text-foreground"
            >
              Get started
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
